import { type ReactNode } from 'react'
import { AvatarBlurhash, type AvatarSize } from 'app/components/AvatarBlurhash'
import { isDefined, isNotDefined, type TeamData, type TinyTeam } from '@terros/common'
import { Group, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core'
import {
  getInitialism,
  getLevelName,
  LOCKED_FIELD_MESSAGE,
  useCanUpdateSunrunTeam,
  useLockedTeamFields,
  usePermissionCheck,
  useProfile,
  useTeam,
  useTeamAvatarUploadUrl,
  useTranslation,
  SizePrimitives,
} from '@front/model'
import { UpdateTeamAvatarButton } from '../UpdateAvatarButton'
import { useEditTeamField } from './useEditTeamField'

const INTERACTIVE = 'rounded-sm hover:bg-neutral-light-hover focus-visible:bg-neutral-light-hover'

type Props = {
  team: TinyTeam
  size?: AvatarSize
}

export const TeamTitle = ({ team: tinyTeam, size = 'sm' }: Props): ReactNode => {
  const { company } = useProfile()
  const { data } = useTeam(tinyTeam.teamId)
  const { data: canUpdate } = usePermissionCheck({ permissionType: 'team:update', targetId: tinyTeam.teamId })
  const { data: upload } = useTeamAvatarUploadUrl(tinyTeam.teamId, canUpdate === true)
  const team = data?.team
  const levelName = isDefined(tinyTeam.level) ? getLevelName(company, tinyTeam.level) : undefined

  return (
    <Group align='center' gap='sm' miw={0} wrap='nowrap'>
      {team ? (
        <UpdateTeamAvatarButton
          avatarBlurhash={team.avatarBlurhash}
          avatarUrl={team.avatarUrl}
          disabled={team.isDeleted || !canUpdate || isNotDefined(upload?.uploadAvatarUrl)}
          initials={getInitialism(team.name)}
          size={size}
          teamId={team.teamId}
          uploadAvatarUrl={upload?.uploadAvatarUrl}
        />
      ) : (
        <AvatarBlurhash
          avatarBlurhash={tinyTeam.avatarBlurhash}
          avatarUrl={tinyTeam.avatarUrl}
          initials={getInitialism(tinyTeam.name)}
          size={size}
        />
      )}
      <Stack flex={1} gap={0} miw={0}>
        {levelName && (
          <Text c='dimmed' size='xs' truncate='end'>
            {levelName}
          </Text>
        )}
        <Group mih={SizePrimitives['9']} miw={0} wrap='nowrap'>
          {team ? <EditableTeamName team={team} /> : <TeamName>{tinyTeam.name}</TeamName>}
        </Group>
      </Stack>
    </Group>
  )
}

const TeamName = ({ children }: { children: string }): ReactNode => (
  <Text className='break-words' fw='bold' size='lg'>
    {children}
  </Text>
)

const EditableTeamName = ({ team }: { team: TeamData }): ReactNode => {
  const { t } = useTranslation()
  const { company } = useProfile()
  const canUpdateName = useCanUpdateSunrunTeam(company?.companyId, team)
  const { lockedFields, isLoading: isLoadingLocks } = useLockedTeamFields(team.teamId)
  const isNameLocked = lockedFields.includes('name')
  const edit = useEditTeamField(team, {
    label: 'Name',
    required: true,
    disabled: !canUpdateName || isLoadingLocks,
    getValue: ({ name }) => name,
    toUpdates: (name) => ({ name }),
  })

  if (edit.editor && !isNameLocked) return edit.editor
  if (edit.disabled || !edit.onEdit) return <TeamName>{team.name}</TeamName>
  if (isNameLocked)
    return (
      <Tooltip label={t(LOCKED_FIELD_MESSAGE)}>
        <div>
          <TeamName>{team.name}</TeamName>
        </div>
      </Tooltip>
    )

  return (
    <Tooltip label={t('Edit name')}>
      <UnstyledButton className={INTERACTIVE} maw='100%' miw={0} type='button' onClick={edit.onEdit}>
        <TeamName>{team.name}</TeamName>
      </UnstyledButton>
    </Tooltip>
  )
}
